import React, { useEffect, useMemo, useState } from "react";
import { FaUser, FaFileAlt, FaPhone, FaComment } from "react-icons/fa";
import { format } from "date-fns";

const AssignmentMainContent = ({
  assignments,
  selectedAssignmentId,
  searchTerm,
  onSelectAssignment,
  isRegexMode,
  rowClassName,
}) => {
  const [assignmentCount, setAssignmentCount] = useState(0);

  const filteredAssignments = useMemo(() => {
    if (!searchTerm) {
      return assignments;
    }

    let results = [...assignments];
    let term = searchTerm;

    // Apply special filters if present - using regex to match any string inside curly braces
    const specialFilterRegex = /{([^}]+)}/g;
    const matches = term.match(specialFilterRegex);
    if (matches) {
      const match = matches[0];
      const filterValue = match.replace(/{|}/g, "");
      if (filterValue === "open") {
        // Filter for open assignments
        results = results.filter(
          (assignment) => assignment.state?.open === true,
        );
        // Remove the special filter from the search term
        term = term.replace(`{${filterValue}}`, "").trim();
      } else if (filterValue === "closed") {
        // Filter for closed assignments
        results = results.filter(
          (assignment) => assignment.state?.open === false,
        );
        // Remove the special filter from the search term
        term = term.replace(`{${filterValue}}`, "").trim();
      } else {
        // Filter for specific blueprint
        results = results.filter(
          (assignment) => assignment?.blueprint === filterValue,
        );
        // Remove the special filter from the search term
        term = term.replace(`{${filterValue}}`, "").trim();
      }
    }

    // If we have no term left after removing special filters, return current results
    if (!term) {
      return results;
    }

    // Apply regex or text search to the current results
    if (isRegexMode) {
      try {
        const regex = new RegExp(term, "i");
        return results.filter((item) =>
          Object.values(item).some((value) => {
            const val =
              typeof value === "object" ? JSON.stringify(value) : String(value);
            return regex.test(val);
          }),
        );
      } catch (e) {
        // If regex is invalid, fall back to standard search
        return results.filter((item) =>
          Object.values(item).some((value) => {
            const val = (
              typeof value === "object" ? JSON.stringify(value) : String(value)
            ).toLowerCase();
            return val.includes(term.toLowerCase());
          }),
        );
      }
    } else {
      // Standard text search
      const termLower = term.toLowerCase();
      return results.filter((assignment) =>
        Object.values(assignment).some((value) => {
          const val = (
            typeof value === "object" ? JSON.stringify(value) : String(value)
          ).toLowerCase();
          return val.includes(termLower);
        }),
      );
    }
  }, [assignments, searchTerm, isRegexMode]);

  useEffect(() => {
    const totalAssignments = filteredAssignments?.reduce(
      (total, assignment) => {
        return total + (assignment ? 1 : 0);
      },
      0,
    );
    setAssignmentCount(totalAssignments);
  }, [filteredAssignments]);

  return (
    <div className="flex flex-col h-full text-white w-full">
      <div className="flex-grow overflow-y-auto dark-slim-scrollbar">
        {filteredAssignments?.map((assignment, index) => (
          <div
            key={assignment.id}
            className={`border-b border-gray-700 ${index === filteredAssignments.length - 1 ? "border-b-0" : ""} ${rowClassName || ""}`}
          >
            <div
              className={`p-3 cursor-pointer ${
                selectedAssignmentId === assignment.id
                  ? "bg-blue-600 bg-opacity-50"
                  : "hover:bg-gray-700"
              }`}
              onClick={() => onSelectAssignment(assignment)}
            >
              <div className="flex justify-between text-xs">
                <span className="text-gray-300">
                  {assignment.created_at
                    ? format(new Date(assignment.created_at), "M/d/yy h:mm a")
                    : "N/A"}
                </span>
                <span className={`font-mono flex items-center text-green-400`}>
                  <FaFileAlt
                    className={`inline mr-1 text-green-400`}
                    size={12}
                  />
                  <span title={assignment.id || "No ID"}>{assignment.id}</span>
                </span>
              </div>
              <div className="flex justify-between my-2 text-xs">
                <span className="flex items-center text-gray-300">
                  <FaUser className="mr-1 text-xs" />
                  <span className="mr-1">
                    {assignment.created_by || "Unknown"}
                  </span>
                  <span className="mr-1">
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${assignment.state?.open ? "bg-blue-500" : "bg-gray-500"} text-white break-words whitespace-normal max-w-xs flex items-center`}
                    >
                      {assignment.variables?.modality === "voice" ? (
                        <FaPhone className="mr-1" />
                      ) : (
                        <FaComment className="mr-1" />
                      )}
                      {assignment.state?.open
                        ? "open"
                        : `closed: ${assignment.state?.closed_reason || "unknown"}`}
                    </span>
                  </span>
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex row justify-around items-center border-t border-gray-700">
        {assignmentCount > 0 && (
          <div className="p-2 text-xs text-gray-300 text-center">
            {assignmentCount} assignment
            {assignmentCount !== 1 ? "s" : ""}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignmentMainContent;
